import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { StatusBadge } from "../components/StatusBadge";
import { StartWalkthroughButton } from "../components/Walkthrough";
import { KatlasModeChip, useKatlasStatus } from "../components/Katlas";
import { api } from "../lib/api";
import { formatGBP } from "../lib/format";
import {
  ArrowRight, Flag, Trophy, Sparkles, ChevronDown, ChevronUp, Gauge, Users,
  ShieldCheck, Wallet, Handshake, ShoppingBag, FileCheck2, Network
} from "lucide-react";

export default function TalentDashboard() {
  const [drivers, setDrivers] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGov, setShowGov] = useState(false);
  const katlas = useKatlasStatus();
  const isLive = katlas?.status === "active";

  useEffect(() => {
    Promise.all([api.listDrivers(), api.listEvents()])
      .then(([d, ev]) => { setDrivers(d); setEvents(ev); setLoading(false); });
  }, []);

  const stats = useMemo(() => {
    const totalGap = drivers.reduce((a, d) => a + (d.funding_need?.gap || 0), 0);
    const ready = drivers.filter((d) => d.sponsor_readiness?.ready_to_share).length;
    return { totalGap, ready, total: drivers.length };
  }, [drivers]);

  return (
    <div className="space-y-12" data-testid="talent-dashboard-page">
      {/* HERO */}
      <section className="relative overflow-hidden rounded-2xl border border-border bg-white">
        <div className="absolute inset-0 pointer-events-none">
          <div className="checker-stripe absolute top-0 right-0 w-72 h-72" />
        </div>
        <div className="relative grid lg:grid-cols-5 gap-8 p-8 sm:p-10">
          <div className="lg:col-span-3 space-y-5">
            <div className="flex items-center gap-2">
              <span className="signal-bar" />
              <span className="tracking-eyebrow text-muted-foreground">Junior Motorsport · Talent platform</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-[56px] tracking-tight font-semibold leading-[1.02]">
              Performance gets attention.<br />
              <span className="signal-text">Evidence earns support.</span>
            </h1>
            <p className="text-base text-muted-foreground max-w-2xl leading-relaxed">
              Help promising drivers turn results, conduct, media and readiness into a credible sponsor story —
              while keeping safeguarding and duty-of-care quietly in place underneath.
            </p>
            <div className="flex flex-wrap items-center gap-3 pt-1">
              <StartWalkthroughButton />
              <Link to="#drivers" onClick={(e) => { e.preventDefault(); document.getElementById("drivers")?.scrollIntoView({ behavior: "smooth" }); }}>
                <Button size="lg" variant="outline" data-testid="hero-view-drivers"><Trophy className="h-4 w-4 mr-1.5" /> View drivers</Button>
              </Link>
              <Link to="/sessions">
                <Button size="lg" variant="ghost" data-testid="hero-view-sessions">Weekend sessions <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
              </Link>
            </div>
          </div>

          <Card className="lg:col-span-2 p-6 border-border race-card bg-[#FAFAFB]">
            <div className="flex items-center justify-between">
              <div className="tracking-eyebrow text-muted-foreground">5-minute walkthrough</div>
              <span className="text-[10px] tracking-eyebrow font-mono-ui px-1.5 py-0.5 rounded border border-border bg-white">Demo</span>
            </div>
            <ol className="mt-3 space-y-2 text-[14px] leading-relaxed">
              <li className="flex gap-3">
                <span className="font-mono-ui text-muted-foreground shrink-0">01</span>
                <Link to="/drivers/d-marcus" className="hover:underline" data-testid="walk-step-1">Open Marcus Reid's driver profile — performance, media, funding ask.</Link>
              </li>
              <li className="flex gap-3">
                <span className="font-mono-ui text-muted-foreground shrink-0">02</span>
                <Link to="/sponsors/s-pole-position" className="hover:underline" data-testid="walk-step-2">Inspect Pole Position's verified sponsor credentials &amp; aims.</Link>
              </li>
              <li className="flex gap-3">
                <span className="font-mono-ui text-muted-foreground shrink-0">03</span>
                <Link to="/inbox" className="hover:underline" data-testid="walk-step-3">Open the officer inbox — proposed introductions with AI alignment.</Link>
              </li>
              <li className="flex gap-3">
                <span className="font-mono-ui text-muted-foreground shrink-0">04</span>
                <Link to="/sessions" className="hover:underline" data-testid="walk-step-4">Glance at session readiness — duty-of-care quietly in place.</Link>
              </li>
            </ol>
            <button
              onClick={() => setShowGov((s) => !s)}
              className="mt-4 inline-flex items-center text-xs text-muted-foreground hover:text-foreground"
              data-testid="toggle-governance-details"
            >
              {showGov ? <ChevronUp className="h-3.5 w-3.5 mr-1" /> : <ChevronDown className="h-3.5 w-3.5 mr-1" />}
              How governance fits in
            </button>
            {showGov && (
              <div className="mt-3 text-xs text-muted-foreground leading-relaxed space-y-2 border-t border-border pt-3">
                <p>AI helps surface alignment and flags. A named club officer approves introductions, defines what's shared, and receipts the decision.</p>
                <p className="font-mono-ui text-[10px] tracking-wide">AI recommends · Authorised humans decide · The club keeps the evidence.</p>
              </div>
            )}
          </Card>
        </div>
      </section>

      {/* Snapshot stats */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Drivers in programme" value={stats.total} Icon={Users} />
        <StatCard label="Sponsor-ready profiles" value={`${stats.ready} / ${stats.total}`} Icon={Sparkles} />
        <StatCard label="Aggregate funding gap" value={formatGBP(stats.totalGap)} Icon={Gauge} />
        <StatCard label="Sessions this weekend" value={events.length} Icon={Flag} link="/sessions" />
      </section>

      {/* Live KATLAS proof — flagship card. Routes to the sponsor approval path. */}
      <section data-testid="live-katlas-proof-card">
        <Card className="border-[#F25E1D]/40 race-card bg-[#FFF7F2] p-6 sm:p-8 relative overflow-hidden">
          <div className="checker-stripe absolute -top-6 -right-6 w-40 h-40 opacity-50 pointer-events-none" />
          <div className="relative grid lg:grid-cols-5 gap-6 items-center">
            <div className="lg:col-span-3 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="signal-bar" />
                <span className="tracking-eyebrow text-muted-foreground">Live KATLAS proof</span>
                <KatlasModeChip size="lg" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight leading-tight">
                Approve one action and see the KATLAS receipt:
                <span className="signal-text"> who approved it, when, what was shared, what was withheld, and the node proof.</span>
              </h2>
              <p className="text-[13px] text-muted-foreground max-w-2xl leading-relaxed">
                The app runs the workflow. AI helps structure the match. Wallets and roles establish custody and
                authority. KATLAS signs the receipt proving who approved what, when, what was shared, and what was withheld.
              </p>
              <div className="flex flex-wrap gap-2 pt-1">
                <Link to="/inbox?id=MX-INIT-001">
                  <Button size="lg" data-testid="live-katlas-proof-cta">
                    <Network className="h-4 w-4 mr-1.5" /> See live receipt flow
                    <ArrowRight className="h-4 w-4 ml-1.5" />
                  </Button>
                </Link>
                <Link to="/katlas-capability">
                  <Button size="lg" variant="outline" data-testid="live-katlas-proof-secondary">
                    Read the CAR explainer
                  </Button>
                </Link>
              </div>
            </div>
            <div className="lg:col-span-2">
              <Card className="border-border bg-white p-4 space-y-2">
                <div className="tracking-eyebrow text-muted-foreground">What you'll see</div>
                <ol className="text-[13px] space-y-1.5">
                  <li className="flex gap-2"><span className="font-mono-ui text-muted-foreground shrink-0">01</span>Open the officer Inbox — Marcus × Pole Position, 92% alignment.</li>
                  <li className="flex gap-2"><span className="font-mono-ui text-muted-foreground shrink-0">02</span>Approve as Club Secretary — wallet + role custody/authority.</li>
                  <li className="flex gap-2"><span className="font-mono-ui text-muted-foreground shrink-0">03</span>Receipt shows real KATLAS-signed proof: hash, node, timestamp.</li>
                  <li className="flex gap-2"><span className="font-mono-ui text-muted-foreground shrink-0">04</span>Public share redacts withheld evidence by count only.</li>
                </ol>
                <div className="text-[11px] text-muted-foreground italic pt-1 border-t border-border mt-2">
                  {isLive ? "KATLAS node active — every approval signs a real receipt." : "Running in governance simulation in this environment — operating model only."}
                </div>
              </Card>
            </div>
          </div>
        </Card>
      </section>

      {/* Demo path — visible hub linking every demo capability in one click */}
      <section className="space-y-4" data-testid="demo-path-hub">
        <div>
          <div className="tracking-eyebrow text-muted-foreground">Demo path · Start here</div>
          <h2 className="text-2xl font-semibold mt-1">Open any card to see that part of ClubCierge in action</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl leading-relaxed">
            <b>Most club sites publish profiles, products and opportunities. ClubCierge turns key actions into accountable,
            real-time governed activity.</b> When a sponsor introduction, parent approval or support claim happens, KATLAS
            signs the receipt: who approved what, when, what was shared, and what was withheld.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-3">
          <DemoCard to="/katlas-capability" testid="demo-card-katlas-capability" Icon={ShieldCheck}
            kicker="01 · Capability"
            title="KATLAS capability"
            body="What the app does, what AI does, what KATLAS does — and what changes when a live node is connected." />
          <DemoCard to="/drivers/d-marcus" testid="demo-card-talent-wallet" Icon={Wallet}
            kicker="02 · Talent wallet"
            title="Marcus Reid — driver profile"
            body="Member-controlled profile: performance, sponsor readiness and wallet proofs without exposing raw data." />
          <DemoCard to="/inbox?id=MX-INIT-001" testid="demo-card-sponsor-intro" Icon={Handshake}
            kicker="03 · Sponsor introduction"
            title="Approve Marcus × Pole Position"
            body="92% alignment · needs_review. Approve as Club Secretary to create the receipt and share token." />
          <DemoCard to="/drivers/d-marcus/offers" testid="demo-card-eligible-support" Icon={ShoppingBag}
            kicker="04 · Bounded shop"
            title="Eligible support &amp; kit"
            body="4 offers gated by role, age band, sponsor introduction and parent + officer approval. No checkout." />
          <DemoCard to="/receipts" testid="demo-card-receipts" Icon={FileCheck2}
            kicker="05 · Receipts &amp; proofs"
            title="Receipts and proofs"
            body="Every governed action leaves a receipt: action, role, time, shared / withheld, hash, share token." />
        </div>
        <div className="text-[12px] text-muted-foreground italic" data-testid="katlas-env-footer">
          {isLive
            ? "KATLAS node active. Every governed action is signed by a real KATLAS receipt."
            : "This environment runs in KATLAS governance simulation mode. It shows the operating model and the minimal payload that would be submitted to a live node."}
        </div>
      </section>

      {/* Satellite club examples — secondary section, smaller than the main proof. */}
      <section className="space-y-4" data-testid="satellite-club-section">
        <div>
          <div className="tracking-eyebrow text-muted-foreground">Satellite club examples</div>
          <h2 className="text-xl font-semibold mt-1">Same KATLAS governance pattern. Different club context.</h2>
          <p className="text-[13px] text-muted-foreground mt-1 max-w-3xl leading-relaxed">
            Each example shows how a member organisation can turn normal club activity into accountable, receipt-backed action: who controls the evidence, who has authority to act, what is shared, what is withheld, and what KATLAS signs.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-3" data-testid="satellite-cards">
          <SatelliteCard
            kind="classic"
            accent="#A24F2A"
            title="Classic car club"
            workingName="ClassicCierge"
            proofLine="Vehicle, event and benefit eligibility with signed evidence receipts."
            cta="Open ClassicCierge"
          />
          <SatelliteCard
            kind="rider"
            accent="#5E2B1D"
            title="Rider club"
            workingName="RiderCierge"
            proofLine="Ride-outs, rallies and member permissions with accountable approvals."
            cta="Open RiderCierge"
          />
          <SatelliteCard
            kind="training"
            accent="#1F4A4A"
            title="Training / awareness centre"
            workingName="TrainingCierge"
            proofLine="Role-gated access, supervision and evidence boundaries for professional settings."
            cta="Open TrainingCierge"
          />
        </div>
        <div className="text-[11px] text-muted-foreground italic">
          One backbone. Three satellites. Same KATLAS proof.
        </div>
      </section>
      <section id="drivers" className="space-y-5">
        <div className="flex items-baseline justify-between">
          <div>
            <div className="tracking-eyebrow text-muted-foreground">Drivers</div>
            <h2 className="text-2xl font-semibold mt-1">Talent in support</h2>
            <p className="text-sm text-muted-foreground mt-1">Performance, profile and readiness at a glance. Open a driver to build a sponsor pack.</p>
          </div>
        </div>
        {loading && <Card className="p-8 text-sm text-muted-foreground">Loading drivers…</Card>}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {drivers.map((d) => <DriverCard key={d.id} d={d} />)}
        </div>
      </section>

      {/* Upcoming sessions secondary */}
      <section className="space-y-3">
        <div className="flex items-baseline justify-between">
          <div>
            <div className="tracking-eyebrow text-muted-foreground">Operational readiness</div>
            <h2 className="text-2xl font-semibold mt-1">This weekend's sessions</h2>
          </div>
          <Link to="/sessions" className="text-sm underline-offset-4 hover:underline" data-testid="see-all-sessions">See all sessions →</Link>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {events.slice(0, 3).map((ev) => (
            <Link key={ev.id} to={`/events/${ev.id}`} className="block" data-testid={`talent-session-${ev.id}`}>
              <Card className="p-4 border-border race-card hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-[15px] font-semibold truncate">{ev.title}</div>
                    <div className="text-[11px] text-muted-foreground font-mono-ui mt-0.5">{ev.location}</div>
                  </div>
                  <StatusBadge status={ev.readiness.overall} />
                </div>
                <div className="text-[11px] text-muted-foreground mt-3">
                  {ev.readiness.open_exception_count} open · {ev.readiness.ready_to_proceed ? "ready" : ev.approval_status === "approved" ? "approved" : "awaiting sign-off"}
                </div>
              </Card>
            </Link>
          ))}
        </div>
      </section>

      {/* Benefits — kept light, secondary to the main story */}
      <section className="space-y-4" data-testid="benefits-section">
        <div>
          <div className="tracking-eyebrow text-muted-foreground">Secondary · For club site owners</div>
          <h2 className="text-2xl font-semibold mt-1">Less guesswork. More controlled action.</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
            ClubCierge does not remove the club's legal or safeguarding responsibilities. It helps the club
            manage them more consistently by making roles, permissions, approvals and evidence easier to
            control and review.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <Card className="p-5 border-border race-card">
            <div className="tracking-eyebrow text-muted-foreground">Before</div>
            <ul className="text-sm mt-2 space-y-1.5 text-muted-foreground">
              <li>Static profiles</li>
              <li>Manual admin</li>
              <li>Scattered messages</li>
              <li>Unclear permissions</li>
              <li>Unverifiable claims</li>
              <li>Hard-to-reconstruct decisions</li>
            </ul>
          </Card>
          <Card className="p-5 border-border race-card bg-white">
            <div className="tracking-eyebrow text-[#F25E1D]">With ClubCierge</div>
            <ul className="text-sm mt-2 space-y-1.5">
              <li>Wallet-backed roles</li>
              <li>AI-assisted workflows</li>
              <li>Parent / officer approval gates</li>
              <li>Controlled sharing</li>
              <li>Sponsor &amp; supplier suitability checks</li>
              <li>Receipts for sensitive actions</li>
            </ul>
          </Card>
          <Card className="p-5 border-border race-card">
            <div className="tracking-eyebrow text-muted-foreground">Outcome</div>
            <ul className="text-sm mt-2 space-y-1.5">
              <li>Less admin friction</li>
              <li>Less unnecessary data exposure</li>
              <li>Clearer responsibility</li>
              <li>Better sponsor confidence</li>
              <li>Safer junior interactions</li>
              <li>Reusable trust pattern across clubs</li>
            </ul>
          </Card>
        </div>
        <div className="text-[12px] text-muted-foreground font-mono-ui pt-1">
          ClubCierge does not make risk disappear. It makes important actions permissioned, reviewable and evidenced.
        </div>
      </section>
    </div>
  );
}

function SatelliteCard({ kind, accent, title, workingName, proofLine, cta }) {
  return (
    <Link to={`/satellites/${kind}`} className="block" data-testid={`satellite-card-${kind}`}>
      <Card
        className="race-card p-4 border-2 hover:shadow-md transition-shadow h-full flex flex-col gap-2"
        style={{ borderColor: `${accent}55`, background: `linear-gradient(135deg, ${accent}10 0%, #FFFFFF 70%)` }}
      >
        <div className="tracking-eyebrow text-muted-foreground">{title}</div>
        <div className="text-base font-semibold" style={{ color: accent }}>{workingName}</div>
        <div className="text-[12px] text-foreground/80 leading-relaxed">{proofLine}</div>
        <div className="mt-auto pt-2 border-t border-border/60 text-[11px] text-muted-foreground flex flex-wrap gap-x-2 items-center">
          <span>Custody · Authority · Receipt</span>
        </div>
        <div className="flex items-center justify-end text-[12px] font-medium" style={{ color: accent }}>
          {cta} <ArrowRight className="h-3.5 w-3.5 ml-1" />
        </div>
      </Card>
    </Link>
  );
}


function DemoCard({ to, testid, Icon, kicker, title, body }) {
  return (
    <Link to={to} data-testid={testid} className="block">
      <Card className="p-4 border-border race-card hover:shadow-md transition-shadow h-full flex flex-col gap-2 bg-white">
        <div className="flex items-start justify-between gap-2">
          <div className="tracking-eyebrow text-muted-foreground">{kicker}</div>
          <Icon className="h-4 w-4 text-[#F25E1D] shrink-0" />
        </div>
        <div className="text-[15px] font-semibold leading-tight">{title}</div>
        <div className="text-[12px] text-muted-foreground leading-relaxed">{body}</div>
        <div className="text-[12px] text-[#F25E1D] inline-flex items-center mt-auto pt-1">
          Open <ArrowRight className="h-3 w-3 ml-1" />
        </div>
      </Card>
    </Link>
  );
}


function StatCard({ label, value, Icon, link }) {
  const body = (
    <Card className="p-5 border-border race-card flex items-center gap-4">
      <div className="h-10 w-10 rounded-md grid place-items-center bg-[#131318] text-white">
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] tracking-eyebrow text-muted-foreground">{label}</div>
        <div className="text-xl font-semibold mt-0.5 truncate">{value}</div>
      </div>
    </Card>
  );
  return link ? <Link to={link} data-testid={`stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>{body}</Link> : body;
}

function DriverCard({ d }) {
  const sr = d.sponsor_readiness;
  const fund = d.funding_need || {};
  const fundPct = fund.total_required ? Math.min(100, Math.round((fund.secured / fund.total_required) * 100)) : 0;
  return (
    <Link to={`/drivers/${d.id}`} className="block group" data-testid={`driver-card-${d.id}`}>
      <Card className="p-5 border-border race-card flex flex-col gap-4 group-hover:shadow-md transition-shadow">
        <div className="flex items-start gap-4">
          <div className="relative">
            <img
              src={d.avatar_url}
              alt=""
              className="h-16 w-16 rounded-md object-cover bg-muted"
              onError={(e) => { e.currentTarget.style.background = "linear-gradient(135deg,#1A1A1F,#3A3A45)"; e.currentTarget.removeAttribute("src"); }}
            />
            <span className="absolute -bottom-1 -right-1 h-7 min-w-7 px-1 rounded-md bg-[#F25E1D] text-white text-[12px] font-semibold grid place-items-center font-mono-ui">
              #{d.race_number}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[16px] font-semibold truncate">{d.name}</div>
            <div className="text-[12px] text-muted-foreground">{d.series}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5 font-mono-ui">Age {d.age} · {d.location}</div>
          </div>
        </div>

        <div>
          <div className="flex items-end justify-between text-[11px] mb-1">
            <span className="tracking-eyebrow text-muted-foreground">Sponsor readiness</span>
            <span className="font-mono-ui font-semibold" style={{ color: sr.score >= 80 ? "var(--rag-green-text)" : sr.score >= 60 ? "var(--rag-amber-text)" : "var(--rag-red-text)" }}>
              {sr.score}%
            </span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-secondary overflow-hidden">
            <div className="h-full rounded-full" style={{
              width: `${sr.score}%`,
              backgroundColor: sr.score >= 80 ? "var(--rag-green-text)" : sr.score >= 60 ? "var(--rag-amber-text)" : "var(--rag-red-text)"
            }} />
          </div>
        </div>

        <div className="flex items-end justify-between">
          <div>
            <div className="text-[11px] tracking-eyebrow text-muted-foreground">Funding gap</div>
            <div className="text-lg font-semibold">{formatGBP(fund.gap)}</div>
            <div className="text-[10px] text-muted-foreground font-mono-ui">{formatGBP(fund.secured)} of {formatGBP(fund.total_required)} secured · {fundPct}%</div>
          </div>
          <div className="text-right">
            <div className="text-[11px] tracking-eyebrow text-muted-foreground">Operational</div>
            <StatusBadge status={d.upcoming?.readiness?.overall || "neutral"} testId={`driver-op-${d.id}`} />
          </div>
        </div>

        <div className="flex items-center justify-between border-t border-border pt-3">
          <span className="text-xs text-muted-foreground">
            {sr.ready_to_share ? "Ready to share" : `${sr.items.filter((i) => !i.ok).length} items left`}
          </span>
          <span className="text-sm font-medium inline-flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
            Open profile <ArrowRight className="h-3.5 w-3.5" />
          </span>
        </div>
      </Card>
    </Link>
  );
}
